import styled, { css } from 'styled-components'
import { Container } from 'src/components/common/Container'
import { ellip, layoutMaxSize } from 'src/styles/mixin'
import { CouponMapProps, storeMarkerType } from 'src/pages/CouponMapPage'
import { Typography } from 'src/components/common/Typography'
import { Noto_body2_b14, Noto_body2_r14, Noto_heading3_b20 } from 'src/styles/fonts/NotoSansKrFonts'
import { Icon } from 'src/assets/svg/Icon'
import Button, { ButtonOption1 } from 'src/components/common/Button'
import React, { useEffect, useRef } from 'react'
import { CustomSwiper } from 'src/components/common/CustomSwiper'
import { SwiperRef } from 'swiper/swiper-react'
import { Chips } from 'src/components/common/Chips'
import { useAnimationControls } from 'framer-motion'
import { motion } from 'framer-motion'
import RenderIf from 'src/components/common/RenderIf'
import usePageNav from 'src/hooks/usePageNav.hook'
import { useGlobalStore } from 'src/store/zustand'
import { Bridge } from 'src/utils/bridge'

const StoreInfoCard = ({
  selectedStore,
  storeMarkers,
  ownedCouponCntByBrand,
  map,
  handleUpdateActiveStoreCard,
  brandList,
  filterBrand,
}: CouponMapProps) => {
  const { place_name, brandNumber, search_keyword, categoryNumber } = selectedStore!
  const { brandSalePageNav, brandPossessionPageNav } = usePageNav()
  const swiperRef = useRef<SwiperRef>(null)
  const storeCardControl = useAnimationControls()
  const { setBrandUnderLineTab } = useGlobalStore()

  const filterStoreMarker = !filterBrand
    ? storeMarkers
    : storeMarkers?.filter((w) => w.brandNumber === filterBrand?.brandNumber)

  const saleBrandCouponCnt = (
    brandList
      ?.map((category) => category.BrandList.find((brand) => brand.BrandNumber === brandNumber))
      .filter(Boolean)[0] || null
  )?.CouponCnt

  const handleStoreCardAnimation = {
    initial: { opacity: 0, display: 'none' },
    open: () =>
      storeCardControl?.start({ opacity: 1, display: 'flex', transition: { duration: 0.6 } }),
    close: () =>
      storeCardControl
        ?.start({ opacity: 0, display: 'none', transition: { duration: 0.6 } })
        .then(handleUpdateActiveStoreCard.reset),
  }

  const swiperClickTrigger = () => {
    const categoryOrder = filterStoreMarker?.findIndex((w) => w.place_name === place_name)

    if (swiperRef.current) {
      swiperRef?.current?.swiper?.slideTo?.(categoryOrder)
    }
  }

  const handleOwnedCouponUse = () => {
    setBrandUnderLineTab(1)
    brandPossessionPageNav(categoryNumber, brandNumber, search_keyword)
  }

  const handleSaleCouponUse = () => {
    brandSalePageNav(categoryNumber, brandNumber, search_keyword)
    setBrandUnderLineTab(0)
  }

  useEffect(() => {
    if (!!selectedStore) {
      swiperClickTrigger()
      if ((map?.getLevel() as number) > 5) {
        map?.setLevel(6)
      } else map?.setLevel((map?.getLevel() as number) + 1)

      setTimeout(() => {
        map?.panTo(new kakao.maps.LatLng(selectedStore.position.lat, selectedStore.position.lng))
        map?.relayout()
      }, 100)
      handleStoreCardAnimation.open()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore])

  return (
    <motion.div animate={storeCardControl} initial={handleStoreCardAnimation.initial}>
      <RenderIf when={filterStoreMarker?.length !== 0}>
        <CustomSwiper.Wrap
          type="default"
          ref={swiperRef}
          gap={10}
          swiperOption={{ centeredSlides: true }}
          $wrapstyle={S.BodyCouponSwiperStyle}
          onActiveIndexChangeFn={(swiperActiveIdx: number) =>
            handleUpdateActiveStoreCard.set(
              (filterStoreMarker as storeMarkerType[])?.[swiperActiveIdx],
            )
          }
        >
          {filterStoreMarker?.map((store, s_i: number) => (
            <CustomSwiper.Item $itemstyle={S.BodyCouponSwiperItemsStyle} key={s_i}>
              <S.StorePlaceNameContainer $option="flex_row" $column_gap="7px">
                <S.StoreInfoPlaceName $fontStyle={Noto_heading3_b20}>
                  {store.place_name}
                </S.StoreInfoPlaceName>
                <Icon.OpenWebsite
                  onClickFn={() => Bridge.handleReqExternalOpenUrl(store.place_url)}
                />
              </S.StorePlaceNameContainer>
              <Container $option="flex_row_wrap" $column_gap="8px">
                <RenderIf when={store.distance !== '0'}>
                  <Typography $fontStyle={Noto_body2_b14}>{store.distance}</Typography>
                </RenderIf>
                <Typography $fontStyle={Noto_body2_r14}>{store.address_name}</Typography>
              </Container>
              <S.StoreInfoChipsContainer $option="flex_row" $column_gap="4px">
                <RenderIf when={!!ownedCouponCntByBrand}>
                  <Chips.Item size="little" style={S.PossessionChipsStyle}>
                    +{ownedCouponCntByBrand} 보유중
                  </Chips.Item>
                </RenderIf>
                <RenderIf when={!!saleBrandCouponCnt}>
                  <Chips.Item size="little" style={S.SaleChipsStyle}>
                    +{saleBrandCouponCnt} 판매중
                  </Chips.Item>
                </RenderIf>
              </S.StoreInfoChipsContainer>
              <S.StoreInfoButtonContainer $option="flex_row" $column_gap="10px">
                <Button
                  option={ButtonOption1('default')}
                  disabled={!ownedCouponCntByBrand}
                  onClickFn={handleOwnedCouponUse}
                >
                  {!!ownedCouponCntByBrand ? '보유쿠폰 사용' : '보유쿠폰 없음'}
                </Button>
                <Button
                  option={ButtonOption1('active')}
                  onClickFn={handleSaleCouponUse}
                  disabled={!saleBrandCouponCnt}
                >
                  할인 쿠폰 구매
                </Button>
              </S.StoreInfoButtonContainer>
              <S.StoreInfoCardClose $option="flex_row" onClick={handleStoreCardAnimation.close}>
                <Icon.Arrow size="16" rotate="bottom" color="var(--black-20)" />
              </S.StoreInfoCardClose>
            </CustomSwiper.Item>
          ))}
        </CustomSwiper.Wrap>
      </RenderIf>
    </motion.div>
  )
}
export default StoreInfoCard

const S = {
  BodyCouponSwiperStyle: css`
    width: 100%;
    display: block;
    position: relative;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 100%);
    padding-bottom: 10px;
  `,
  BodyCouponSwiperItemsStyle: css`
    display: flex;
    flex-direction: column;
    width: 90%;
    box-sizing: border-box;
    justify-content: space-between;
    align-items: center;
    background-color: var(--white);
    border-radius: 24px;
    padding: 20px 20px 0 20px;
    column-gap: 16px;
    height: 100%;
  `,
  StoreInfoCardContainer: styled(Container)`
    border-radius: 20px;
    ${layoutMaxSize};
    position: relative;
    left: 50%;
    transform: translate(-50%, 0%);
    z-index: 3;
    box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.1);
  `,
  StorePlaceNameContainer: styled(Container)`
    align-items: center;
    height: 38px;
    margin-bottom: 2px;
  `,
  StoreInfoContents: styled(Container)`
    padding: 20px;
  `,
  StoreInfoChipsContainer: styled(Container)`
    margin-top: 6px;
    height: 20px;
  `,
  PossessionChipsStyle: css`
    background-color: var(--red-10);
    color: var(--red-30);
    border: none;
  `,
  SaleChipsStyle: css`
    background: var(--black-4);
  `,
  StoreInfoButtonContainer: styled(Container)`
    margin-top: 16px;
  `,
  StoreInfoPlaceName: styled(Typography)`
    ${ellip('line1')};
  `,
  StoreInfoCardClose: styled(Container)`
    justify-content: center;
    padding: 6px 0 10px 0;
  `,
}
